import type {
  ApplicationCommercialEntity,
  ApplicationEntityRelationship,
  ApplicationIndividual,
  ApplicationIndividualAdditionalRoleWrite,
  ApplicationIndividualRole,
  ApplicationIndividualRoleFlags,
} from '../domain/application-parties.domain';
import type { SignerRoleTypes } from '../domain/application-service.domain';

// Fixed order, so two individuals holding the same roles always render identically. 'Sole Trader' is
// the only label that differs from its flag name.
const ROLE_LABELS: [keyof ApplicationIndividualRoleFlags, string][] = [
  ['isDirector', 'Director'],
  ['isPartner', 'Partner'],
  ['isShareholder', 'Shareholder'],
  ['isSoleTrader', 'Sole Trader'],
  ['isTrustee', 'Trustee'],
  ['isSpouse', 'Spouse'],
];

/** Every business role held against one entity. */
export const partyRoleLabels = (role: ApplicationIndividualRoleFlags): string[] =>
  ROLE_LABELS.filter(([flag]) => role[flag]).map(([, label]) => label);

/**
 * Every business role an individual holds, across all their entity relationships.
 *
 * Where apiRoleFromRoleFlags returns only the highest-precedence role and discards the rest, this
 * returns all of them, so a stakeholder who is both a director and a shareholder reads as both. The
 * same applies to an applicant who also holds a role, where the single Role string short-circuits
 * to 'Applicant' alone.
 *
 * Guarantor is included only when the individual holds no other role, matching the Role string's
 * precedence: it is already conveyed by individualSigningLabel, so a substantive role wins.
 */
export const individualRoleLabels = (
  individual: Pick<ApplicationIndividual, 'isApplicant' | 'isGuarantor'> & { roles: ApplicationIndividualRoleFlags[] },
): string[] => {
  const labels = individual.roles.flatMap(partyRoleLabels);
  if (individual.isApplicant) {
    labels.unshift('Applicant');
  }
  if (individual.isGuarantor && !labels.length) {
    labels.push('Guarantor');
  }
  return [...new Set(labels)];
};

/**
 * How the individual signs, for display. Replaces the SignerRole string together with the
 * SignerRolePipe that formats it, and matches its fallback exactly.
 *
 * 'Others' is kept rather than reduced to an empty label: it is the wording the individual dialog's
 * own Role dropdown offers, so it is the term brokers already recognise for a party who neither
 * signs nor guarantees. Note that the choice is not persisted - an explicit 'Others' writes no role
 * row - so on read this is always recomputed from the flags.
 *
 * There is deliberately no 'Corporate Guarantor': the read path cannot produce it.
 * apiSignerRoleFromRoleFlags ignores the linked entity it is handed and returns only
 * GuarantorSigner, Guarantor, Signer or Others. Deriving it here would need to know which entity is
 * the borrower, which an individuals-only payload does not carry.
 */
export const individualSigningLabel = (individual: ApplicationIndividual): string => {
  const signs = individual.roles.some(role => role.isSigner);
  if (individual.isGuarantor) {
    return signs ? 'Guarantor / Signer' : 'Guarantor';
  }
  return signs ? 'Signer' : 'Others';
};

// ---------------------------------------------------------------------------
// Wire adapters. Salesforce, PPSR and Docusign each take a single string where a party holds a set
// of independent flags, so these collapse the set the way those systems expect. The collapse is
// lossy by definition - a director who is also a shareholder reads as one of the two - which is why
// nothing internal should bucket on the result: use the flags, or the label helpers above.
//
// Not deprecated. The external contracts are what they are, and these are the one place each
// collapse is written down, so the rules cannot drift apart across callers.
// ---------------------------------------------------------------------------

export type IndividualRoleType =
  'Applicant' | 'Director' | 'Partner' | 'Shareholder' | 'SoleTrader' | 'Trustee' | 'Spouse' | 'Guarantor' | null;

export type CommercialEntityRoleType = 'Primary' | 'Trustee' | 'Partner' | 'Guarantor';

// Spelt out rather than derived from ROLE_LABELS: these are wire values that must match
// apiRoleFromRoleFlags exactly, where the display labels are free to diverge ('Sole Trader').
const ROLE_TYPE_PRECEDENCE: [keyof ApplicationIndividualRoleFlags, IndividualRoleType][] = [
  ['isDirector', 'Director'],
  ['isPartner', 'Partner'],
  ['isShareholder', 'Shareholder'],
  ['isSoleTrader', 'SoleTrader'],
  ['isTrustee', 'Trustee'],
  ['isSpouse', 'Spouse'],
];

/**
 * The single Role string for an individual. Mirrors apiRoleFromRoleFlags: applicant wins outright,
 * then the headline role's flags in a fixed precedence, then guarantor.
 *
 * Only the primary role is consulted, so a corporate-guarantor link is never promoted into the
 * headline role — which is why ApplicationIndividualRole.isPrimaryRole exists.
 */
export const individualRoleType = (individual: ApplicationIndividual): IndividualRoleType => {
  if (individual.isApplicant) {
    return 'Applicant';
  }
  const primaryRole = individual.roles.find(role => role.isPrimaryRole);
  const match = primaryRole && ROLE_TYPE_PRECEDENCE.find(([flag]) => primaryRole[flag]);
  if (match) {
    return match[1];
  }
  return individual.isGuarantor ? 'Guarantor' : null;
};

/**
 * The single SignerRole string for an individual, for a consumer holding an ApplicationIndividual
 * rather than a stored row.
 *
 * Signing counts only where it is for the borrower: the primary role, or a role against a borrowing
 * entity - a director of the corporate trustee signs for the trust, so is a signer though their role
 * sits on the trustee company. Deriving from `roles.some(role => role.isSigner)` instead would report
 * 'Signer' for someone whose only signing relationship is a corporate-guarantor link, who is 'Others'.
 *
 * Never returns 'CorporateGuarantor': apiSignerRoleFromRoleFlags cannot produce it (see
 * docs/az-1475-party-model.md 10.7), so neither can this.
 *
 * Deliberately not apiSignerRoleFromRoleFlags: that reads one role row's isSigner, so it reports a
 * director of the corporate trustee as a non-signer. The output values are still the same wire
 * vocabulary, and stay so.
 */
export const individualSignerRole = (individual: ApplicationIndividual): SignerRoleTypes => {
  const isSigner = individual.roles.some(role =>
    role.isSigner && (role.isPrimaryRole || !!role.entityIsBorrower));
  if (individual.isGuarantor) {
    return isSigner ? 'GuarantorSigner' : 'Guarantor';
  }
  return isSigner ? 'Signer' : 'Others';
};

/**
 * The single Type string for an entity: primary borrower first, then the *first* relationship only,
 * with everything else falling back to 'Guarantor'.
 *
 * Note it can never return 'Member' or 'Secondary'. Stored rows carrying those types already read
 * back as 'Guarantor' today, so this loses nothing that was not already lost.
 */
export const commercialEntityRoleType = (
  // Structural rather than ApplicationCommercialEntity so the write shape can share it: the two
  // differ only in fields this does not read, and the precedence must not be written twice.
  entity: Pick<ApplicationCommercialEntity, 'isPrimary'> & {
    relationships: readonly Pick<ApplicationEntityRelationship, 'isTrustee' | 'isPartner'>[];
  },
): CommercialEntityRoleType => {
  if (entity.isPrimary) {
    return 'Primary';
  }
  const [relationship] = entity.relationships;
  if (relationship?.isTrustee) {
    return 'Trustee';
  }
  if (relationship?.isPartner) {
    return 'Partner';
  }
  return 'Guarantor';
};

/**
 * The business-search value for a role's linked entity. One place for the fallback rules, shared by
 * the corporate-guarantor form control, the wizard's write payload and the Docusign label.
 *
 * Reads the identifiers denormalised onto the role rather than looking the entity up, so a consumer
 * holding only an individual's roles - the wizard's Individual Details step - can still name the
 * entity by ABN/ACN. The server drops a corporate-guarantor role carrying neither.
 */
export const roleBusinessValue = (
  role: Pick<ApplicationIndividualRole, 'entityLegalName' | 'entityTradingName' | 'entityAbn' | 'entityAcn'>,
): { acn: string; abn?: string; name: string; displayName: string } => ({
  acn: role.entityAcn ?? '',
  abn: role.entityAbn ?? undefined,
  name: role.entityLegalName ?? '',
  displayName: role.entityTradingName ?? role.entityLegalName ?? '',
});

type IndividualEntityRole = {
  business: { acn: string; abn?: string; name: string; displayName: string };
  signerRole: SignerRoleTypes;
};

const toEntityRole = (role: ApplicationIndividualRole): IndividualEntityRole => ({
  business: roleBusinessValue(role),
  signerRole: (role.isSigner ? 'GuarantorSigner' : 'Guarantor') as SignerRoleTypes,
});

/**
 * Every link an individual holds to an entity other than the borrower, in the shape the
 * `additionalRoles` array uses (AZ-1475).
 *
 * Deliberately unfiltered, and deliberately not named for guarantees: a shareholding held against
 * the corporate trustee lands here too. The write shape has no way to describe such a role, so a
 * payload built from this must still name it or the full sync soft-deletes it for being absent.
 * That is why the wizard's write payload uses this rather than individualGuaranteeRoles.
 */
export const individualEntityRoles = (
  individual: Pick<ApplicationIndividual, 'roles'>,
): IndividualEntityRole[] =>
  (individual.roles ?? [])
    .filter(role => !role.isPrimaryRole && role.entityId !== null)
    .map(toEntityRole);

/**
 * The same links in the write shape, stating each stored role's flags and signer status. An echoed
 * entry without flags makes the save infer a guarantee on its entity, and one without isSigner
 * signs by default, so echoing a stored role back must say both or it rewrites what is stored.
 */
export const individualEntityRoleWrites = (
  individual: Pick<ApplicationIndividual, 'roles'>,
): ApplicationIndividualAdditionalRoleWrite[] =>
  (individual.roles ?? [])
    .filter(role => !role.isPrimaryRole && role.entityId !== null)
    .map(role => ({
      business: roleBusinessValue(role),
      flags: {
        isDirector: role.isDirector,
        isPartner: role.isPartner,
        isShareholder: role.isShareholder,
        isSoleTrader: role.isSoleTrader,
        isTrustee: role.isTrustee,
        isSpouse: role.isSpouse,
      },
      isSigner: role.isSigner,
    }));

/**
 * The subset of those links that are actually guarantees, for consumers that mean guarantor rather
 * than "linked to some other entity" - the Docusign signer list above all.
 *
 * Split out because the two were one function, filtered only on "non-primary entity". That held
 * while guaranteeing was the sole way to hold such a link, and stopped holding once KYB began
 * recording roles against trustee and partner entities: a shareholder of the corporate trustee was
 * put on the contract as a guarantor.
 *
 * A role that does not say counts as a guarantee. The server sets entityIsGuarantor on every role
 * it projects, so absent means an older one - and against a stale read, keeping the previous
 * behaviour costs the rare wrongly-listed trustee link, while excluding would drop every genuine
 * corporate guarantor off a contract that needs their signature.
 */
export const individualGuaranteeRoles = (
  individual: Pick<ApplicationIndividual, 'roles'>,
): IndividualEntityRole[] =>
  (individual.roles ?? [])
    .filter(role => !role.isPrimaryRole && role.entityId !== null && role.entityIsGuarantor !== false)
    .map(toEntityRole);

/**
 * Whether an entity role makes its holder a signatory for that entity. KYB records shareholders of
 * a guarantor company with isSigner off; holding the link does not make them a signatory for it.
 */
export const signsGuarantee = (role: { signerRole: SignerRoleTypes }): boolean =>
  role.signerRole === 'Signer' || role.signerRole === 'GuarantorSigner';

/**
 * The guarantees an individual signs, which is what puts them on the contract for a guarantor
 * entity. The approval notice lists guarantor entities, not signatories, so it reads
 * individualGuaranteeRoles instead.
 */
export const individualSignedGuaranteeRoles = (
  individual: Pick<ApplicationIndividual, 'roles'>,
): IndividualEntityRole[] =>
  individualGuaranteeRoles(individual).filter(signsGuarantee);
